import rawRegistry from "../../../shared/safetyqr-reporting-templates.json"
import type { PublicLookupData } from "@/lib/api/client"
import { resolveSafetyQrTagCatalogEntry } from "@/lib/safetyqr-catalog"

export type PlantationLocalLanguageCode =
  | "hi-IN"
  | "kn-IN"
  | "ta-IN"
  | "te-IN"

export interface PlantationLocalLanguageOption {
  code: PlantationLocalLanguageCode
  label: string
}

export const PLANTATION_LOCAL_LANGUAGE_OPTIONS: PlantationLocalLanguageOption[] = [
  { code: "kn-IN", label: "Kannada / ಕನ್ನಡ" },
  { code: "ta-IN", label: "Tamil / தமிழ்" },
  { code: "te-IN", label: "Telugu / తెలుగు" },
  { code: "hi-IN", label: "Hindi / हिंदी" },
]

export type SafetyQrReportingFieldKind =
  | "choice"
  | "email"
  | "select"
  | "tel"
  | "text"
  | "textarea"
  | "toggle"

export interface SafetyQrReportingFieldOption {
  label: string
  value: string
}

export interface SafetyQrReportingFieldDefinition {
  defaultValue?: boolean | string
  helperText?: string
  id: string
  kind: SafetyQrReportingFieldKind
  label: string
  options?: SafetyQrReportingFieldOption[]
  required: boolean
}

export interface SafetyQrReportingTemplateActionDefinition {
  confirmation?: {
    actionLabel: string
    body: string
    title: string
  }
  description: string
  fieldDefaults?: Record<string, boolean | string>
  fieldIds: string[]
  headline?: string
  helperText?: string
  id: string
  label: string
  requiredFieldIds: string[]
  submissionMode: "confirm_before_submit" | "direct_submit"
  submitLabel: string
}

export interface SafetyQrReportingTemplateDefinition {
  confirmation: {
    actionLabel: string
    body: string
    title: string
  }
  actions: SafetyQrReportingTemplateActionDefinition[]
  displayName: string
  fields: SafetyQrReportingFieldDefinition[]
  headline: string
  helperText: string
  id: string
  media: {
    allowVoiceNote: boolean
    maxPhotos: number
  }
  presentation?: {
    localLanguageSelectionMode?: "english_plus_selected_local" | null
    supportsDualLanguage?: boolean
  }
  normalization: {
    delayFieldId?: string
    descriptionFieldIds: string[]
    injuryStatusFieldId?: string
    locationDescriptionFieldId?: string
    peopleInvolvedFieldId?: string
    plantationReferenceFieldId?: string
    reportKind: "behavior" | "incident"
    reporterEmailFieldId?: string
    reporterNameFieldId?: string
    reporterPhoneFieldId?: string
    severityFieldId?: string
    siteContextFieldId?: string
    typeFieldId?: string
  }
  subjectSummary: {
    description: string
    title: string
  }
}

export interface SafetyQrReportingTemplateRegistry {
  registryId: string
  registryVersion: string
  templates: SafetyQrReportingTemplateDefinition[]
}

export const safetyQrReportingTemplateRegistry =
  rawRegistry as SafetyQrReportingTemplateRegistry

interface PlantationLocalizationBundle {
  actions: Record<
    string,
    Partial<SafetyQrReportingTemplateActionDefinition>
  >
  confirmation?: Partial<SafetyQrReportingTemplateDefinition["confirmation"]>
  fields: Record<
    string,
    Partial<SafetyQrReportingFieldDefinition> & {
      optionLabels?: Record<string, string>
    }
  >
  helperText?: string
  headline?: string
  subjectSummary?: Partial<SafetyQrReportingTemplateDefinition["subjectSummary"]>
}

const PLANTATIONS_TEMPLATE_ID = "safetyqr.report.plantations.v1"

const plantationsLocalizationByCode: Record<
  PlantationLocalLanguageCode,
  PlantationLocalizationBundle
> = {
  "kn-IN": {
    headline:
      "ಕೃಷಿ ತೋಟದ ಭದ್ರತಾ ಎಚ್ಚರಿಕೆ ಅಥವಾ ಘಟನೆ ವರದಿ ಕಳುಹಿಸಿ",
    helperText:
      "ಕೃಷಿ ತೋಟದ ವಿವಿಧ ವಲಯಗಳಿಂದ ತ್ವರಿತವಾಗಿ ಭದ್ರತಾ ಎಚ್ಚರಿಕೆ ಅಥವಾ ಘಟನೆ ವರದಿ ಕಳುಹಿಸಲು ಈ ಸರಳ ಕಾರ್ಯಪದ್ಧತಿಯನ್ನು ಬಳಸಿ. GPS ದೊರೆತರೆ ಸೇರಿಸಲಾಗುತ್ತದೆ. ಫೋಟೋಗಳು ಮತ್ತು ಒಂದು ಧ್ವನಿ ಟಿಪ್ಪಣಿ ಆಯ್ಕೆಯಾಗಿದೆ.",
    subjectSummary: {
      title: "ಕೃಷಿ ತೋಟ ವರದಿಗಳು ಸ್ಥಳಾಧಾರಿತವಾಗಿಯೇ ಇರುತ್ತವೆ",
      description:
        "ಪ್ರತಿಕ್ರಿಯೆ ನೀಡುವವರಿಗೆ ಅಗತ್ಯವಾದ ತೋಟ ಮತ್ತು ವಲಯ ಸಂದರ್ಭವನ್ನು ಉಳಿಸಿಕೊಂಡೇ ಸಮಸ್ಯೆ, ಸ್ಥಳ, ಮತ್ತು ಹಿಂತಿರುಗಿ ಸಂಪರ್ಕಿಸಲು ಬೇಕಾದ ಸಂಖ್ಯೆಯನ್ನು ವೇಗವಾಗಿ ಸೆರೆಹಿಡಿಯಿರಿ.",
    },
    confirmation: {
      title: "ಈ ಕೃಷಿ ತೋಟ ವರದಿಯನ್ನು ಈಗ ಕಳುಹಿಸಬೇಕೆ?",
      body: "ಒಮ್ಮೆ ದೃಢೀಕರಿಸಿ, ಕೃಷಿ ತೋಟ ವರದಿಯನ್ನು ತಕ್ಷಣ ಕಳುಹಿಸಿ.",
      actionLabel: "ಕೃಷಿ ತೋಟ ವರದಿಯನ್ನು ದೃಢೀಸಿ ಮತ್ತು ಕಳುಹಿಸಿ",
    },
    actions: {
      panic_alert: {
        label: "ತುರ್ತು ಎಚ್ಚರಿಕೆ",
        headline: "ಕೃಷಿ ತೋಟದ ತುರ್ತು ಎಚ್ಚರಿಕೆ ಕಳುಹಿಸಿ",
        helperText:
          "ನಾವು ತಕ್ಷಣ GPS ಪ್ರಯತ್ನಿಸುತ್ತೇವೆ ಮತ್ತು ಒಂದು ದೃಢೀಕರಣ ಸ್ಪರ್ಶದಿಂದ ಎಚ್ಚರಿಕೆಯನ್ನು ಕಳುಹಿಸುತ್ತೇವೆ. ಫೋಟೋಗಳು ಮತ್ತು ಒಂದು ಧ್ವನಿ ಟಿಪ್ಪಣಿ ಆಯ್ಕೆಯಾಗಿದೆ.",
        description:
          "ತೋಟದಲ್ಲಿ ತುರ್ತು ಅಪಾಯ ಅಥವಾ ಕಾರ್ಮಿಕರ ಸಂಕಷ್ಟಕ್ಕೆ ಬಳಸುವ ಅತಿ ವೇಗದ ಎಚ್ಚರಿಕೆ.",
        submitLabel: "ತುರ್ತು ಎಚ್ಚರಿಕೆ ಕಳುಹಿಸಿ",
      },
      report_incident: {
        label: "ಘಟನೆ ವರದಿ",
        headline: "ಕೃಷಿ ತೋಟದ ಘಟನೆ ವರದಿ ಕಳುಹಿಸಿ",
        helperText:
          "ವಲಯ, ಸಮಸ್ಯೆಯ ವಿಧ, ಸಂಪರ್ಕ ವಿವರಗಳು ಮತ್ತು ಐಚ್ಛಿಕ ಮಾಧ್ಯಮವನ್ನು ರಚನೆಬದ್ಧ ರೀತಿಯಲ್ಲಿ ಸೆರೆಹಿಡಿಯಿರಿ.",
        description:
          "ಕೃಷಿ ತೋಟದ ಕಾರ್ಯಾಚರಣೆ ಮತ್ತು ಅನುವರ್ತನೆಗಾಗಿ ವಿನ್ಯಾಸಗೊಳಿಸಿದ ರಚನೆಬದ್ಧ ಘಟನೆ ವರದಿ.",
        submitLabel: "ಘಟನೆ ವರದಿ ಸಲ್ಲಿಸಿ",
        confirmation: {
          title: "ಈ ಕೃಷಿ ತೋಟ ಘಟನೆ ವರದಿಯನ್ನು ಈಗ ಕಳುಹಿಸಬೇಕೆ?",
          body: "ಒಮ್ಮೆ ದೃಢೀಕರಿಸಿ, ರಚನೆಬದ್ಧ ಕೃಷಿ ತೋಟ ಘಟನೆ ವರದಿಯನ್ನು ತಕ್ಷಣ ಕಳುಹಿಸಿ.",
          actionLabel: "ಘಟನೆ ವರದಿ ಸಲ್ಲಿಸಿ",
        },
      },
    },
    fields: {
      severity: {
        label: "ತುರ್ತುತೆ",
        optionLabels: {
          minor: "ಸಾಮಾನ್ಯ",
          moderate: "ಮಧ್ಯಮ",
          severe: "ತೀವ್ರ",
          critical: "ಅತ್ಯಂತ ತುರ್ತು",
        },
      },
      issueType: {
        label: "ಸಮಸ್ಯೆಯ ವಿಧ",
        optionLabels: {
          medical: "ಕಾರ್ಮಿಕ ಗಾಯ ಅಥವಾ ತುರ್ತು ಸ್ಥಿತಿ",
          hazard: "ಉಪಕರಣ ಅಥವಾ ವಿದ್ಯುತ್ ಅಪಾಯ",
          fire: "ಬೆಂಕಿ ಅಥವಾ ಹೊಗೆ",
          theft: "ಕಾಡುಜೀವಿ ಅಥವಾ ಒಳನುಸುಳುಕೋರ ಅಪಾಯ",
          breakdown: "ವಾಹನ ಅಥವಾ ದಾರಿ ತಡೆ",
          accident: "ಇತರೆ ಕೃಷಿ ತೋಟ ಘಟನೆ",
        },
      },
      locationDescription: {
        label: "ವಲಯ ಅಥವಾ ಸಮೀಪದ ಗುರುತು",
        helperText:
          "GPS ದುರ್ಬಲವಾಗಿದ್ದರೆ ಹೊಲದ ವಲಯ ಹೆಸರು, ಬ್ಲಾಕ್ ಗುರುತು, ರಸ್ತೆ, ಅಥವಾ ಸಮೀಪದ ಗುರುತನ್ನು ಬಳಸಿ.",
      },
      reporterPhone: {
        label: "ಹಿಂತಿರುಗಿ ಸಂಪರ್ಕಿಸುವ ಸಂಖ್ಯೆ",
        helperText:
          "ತ್ವರಿತ ಅನುವರ್ತನೆ ಅಗತ್ಯವಾದರೆ ಮೇಲ್ವಿಚಾರಕರು ಈ ಸಂಖ್ಯೆಯನ್ನು ಬಳಸಬಹುದು.",
      },
      reporterName: {
        label: "ನಿಮ್ಮ ಹೆಸರು",
      },
      reporterEmail: {
        label: "ನಿಮ್ಮ ಇಮೇಲ್",
      },
      plantationReference: {
        label: "ಪ್ಲಾಂಟೇಷನ್, ಕಾರ್ಡ್ ಐಡಿ, ಅಥವಾ ಸೈಟ್ ಉಲ್ಲೇಖ",
      },
      siteContext: {
        label: "ಸೈಟ್ ಸಂದರ್ಭ",
        helperText:
          "ಎಸ್ಟೇಟ್ ಹೆಸರು, ಕಾರ್ಮಿಕ ಸಾಲು, ಸಂಗ್ರಹ ಕೇಂದ್ರ, ಅಥವಾ ನಿಯೋಜನೆ ಟಿಪ್ಪಣಿಯನ್ನು ಸೇರಿಸಿ.",
      },
      description: {
        label: "ಏನು ನಡೆದಿದೆ?",
      },
      policeAssistanceRequested: {
        label: "ಪೊಲೀಸ್ ಅಥವಾ ಭದ್ರತಾ ಸಹಾಯ ಬೇಕಿದೆ",
      },
      ambulanceRequested: {
        label: "ವೈದ್ಯಕೀಯ ಸಾರಿಗೆ ಬೇಕಿದೆ",
      },
    },
  },
  "ta-IN": {
    headline:
      "தோட்ட பாதுகாப்பு அவசர எச்சரிக்கை அல்லது சம்பவ அறிக்கையை அனுப்புங்கள்",
    helperText:
      "தோட்டத்தின் பல பகுதிகளில் இருந்து விரைவாக பாதுகாப்பு எச்சரிக்கை அல்லது சம்பவ அறிக்கை அனுப்ப இந்த எளிய முறையை பயன்படுத்துங்கள். GPS கிடைத்தால் சேர்க்கப்படும். புகைப்படங்களும் ஒரு குரல் குறிப்பும் விருப்பத்திற்குரியது.",
    subjectSummary: {
      title: "தோட்ட அறிக்கைகள் இடவசதிக்கேற்றவையாக இருக்கும்",
      description:
        "பதிலளிப்போருக்கு தேவையான தோட்ட மற்றும் மண்டல விவரங்களை வைத்துக்கொண்டே பிரச்சினை, இடம், மற்றும் தொடர்பு எண்ணை விரைவாக பதிவு செய்யுங்கள்.",
    },
    confirmation: {
      title: "இந்த தோட்ட அறிக்கையை இப்போது அனுப்பவா?",
      body: "ஒருமுறை உறுதிப்படுத்துங்கள். தோட்ட அறிக்கை உடனே அனுப்பப்படும்.",
      actionLabel: "தோட்ட அறிக்கையை உறுதிப்படுத்து மற்றும் அனுப்பு",
    },
    actions: {
      panic_alert: {
        label: "அவசர எச்சரிக்கை",
        headline: "தோட்ட அவசர எச்சரிக்கையை அனுப்புங்கள்",
        helperText:
          "GPS கிடைத்தவுடன் உடனே சேர்த்து, ஒரு உறுதிப்படுத்தலிலேயே எச்சரிக்கையை அனுப்புவோம். புகைப்படங்களும் ஒரு குரல் குறிப்பும் விருப்பத்திற்குரியது.",
        description:
          "தோட்டத்தில் அவசர அபாயம் அல்லது தொழிலாளர் துயர நிலைக்கு மிக வேகமான எச்சரிக்கை.",
        submitLabel: "அவசர எச்சரிக்கையை அனுப்புங்கள்",
      },
      report_incident: {
        label: "சம்பவ அறிக்கை",
        headline: "தோட்ட சம்பவ அறிக்கையை அனுப்புங்கள்",
        helperText:
          "மண்டலம், பிரச்சினை வகை, தொடர்பு விவரங்கள், மற்றும் விருப்ப ஊடகங்களை கட்டமைப்புடன் பதிவு செய்யுங்கள்.",
        description:
          "தோட்ட செயல்பாடுகள் மற்றும் பின்தொடர்வுக்கான கட்டமைக்கப்பட்ட சம்பவ அறிக்கை.",
        submitLabel: "சம்பவ அறிக்கையை சமர்ப்பிக்கவும்",
        confirmation: {
          title: "இந்த தோட்ட சம்பவ அறிக்கையை இப்போது அனுப்பவா?",
          body: "ஒருமுறை உறுதிப்படுத்துங்கள். கட்டமைக்கப்பட்ட தோட்ட சம்பவ அறிக்கை உடனே அனுப்பப்படும்.",
          actionLabel: "சம்பவ அறிக்கையை சமர்ப்பிக்கவும்",
        },
      },
    },
    fields: {
      severity: {
        label: "அவசர நிலை",
        optionLabels: {
          minor: "சிறியது",
          moderate: "மிதமானது",
          severe: "கடுமையானது",
          critical: "மிக அவசரம்",
        },
      },
      issueType: {
        label: "பிரச்சினை வகை",
        optionLabels: {
          medical: "தொழிலாளர் காயம் அல்லது அவசர நிலை",
          hazard: "சாதனம் அல்லது மின்சார அபாயம்",
          fire: "தீ அல்லது புகை",
          theft: "வனவிலங்கு அல்லது உட்புகுதல் அபாயம்",
          breakdown: "வாகனம் அல்லது பாதை தடை",
          accident: "மற்ற தோட்ட சம்பவம்",
        },
      },
      locationDescription: {
        label: "மண்டலம் அல்லது அருகிலுள்ள அடையாளம்",
        helperText:
          "GPS பலவீனமாக இருந்தால் புலம் மண்டல பெயர், பிளாக் குறி, சாலை, அல்லது அருகிலுள்ள அடையாளத்தை பயன்படுத்துங்கள்.",
      },
      reporterPhone: {
        label: "திரும்ப அழைக்க வேண்டிய எண்",
        helperText:
          "விரைவான பின்தொடர்வு தேவைப்பட்டால் மேற்பார்வையாளர்கள் இந்த எண்ணை பயன்படுத்தலாம்.",
      },
      reporterName: {
        label: "உங்கள் பெயர்",
      },
      reporterEmail: {
        label: "உங்கள் மின்னஞ்சல்",
      },
      plantationReference: {
        label: "தோட்டம், அட்டை ஐடி, அல்லது தள குறிப்பு",
      },
      siteContext: {
        label: "தள சூழல்",
        helperText:
          "எஸ்டேட் பெயர், தொழிலாளர் பகுதி, சேகரிப்பு இடம், அல்லது பயன்பாட்டு குறிப்பை சேர்க்கலாம்.",
      },
      description: {
        label: "என்ன நடந்தது?",
      },
      policeAssistanceRequested: {
        label: "போலீஸ் அல்லது பாதுகாப்பு உதவி தேவை",
      },
      ambulanceRequested: {
        label: "மருத்துவ போக்குவரத்து தேவை",
      },
    },
  },
  "te-IN": {
    headline:
      "ప్లాంటేషన్ భద్రతా అత్యవసర అలర్ట్ లేదా ఘటన నివేదికను పంపండి",
    helperText:
      "ప్లాంటేషన్‌లోని వేర్వేరు జోన్ల నుండి త్వరగా భద్రతా అలర్ట్ లేదా ఘటన నివేదిక పంపేందుకు ఈ సరళమైన విధానాన్ని ఉపయోగించండి. GPS అందుబాటులో ఉంటే చేర్చబడుతుంది. ఫోటోలు మరియు ఒక వాయిస్ నోట్ ఐచ్ఛికం.",
    subjectSummary: {
      title: "ప్లాంటేషన్ నివేదికలు ప్రాంగణ సందర్భాన్ని కాపాడుతాయి",
      description:
        "స్పందన బృందానికి అవసరమైన ప్లాంటేషన్ మరియు జోన్ సమాచారాన్ని ఉంచుతూ సమస్య, స్థలం, మరియు తిరిగి సంప్రదించే సంఖ్యను వేగంగా నమోదు చేయండి.",
    },
    confirmation: {
      title: "ఈ ప్లాంటేషన్ నివేదికను ఇప్పుడే పంపాలా?",
      body: "ఒకసారి ధృవీకరించండి. ప్లాంటేషన్ నివేదిక వెంటనే పంపబడుతుంది.",
      actionLabel: "ప్లాంటేషన్ నివేదికను ధృవీకరించి పంపండి",
    },
    actions: {
      panic_alert: {
        label: "అత్యవసర అలర్ట్",
        headline: "ప్లాంటేషన్ అత్యవసర అలర్ట్ పంపండి",
        helperText:
          "GPS అందుబాటులో ఉంటే వెంటనే చేర్చి, ఒక ధృవీకరణతోనే అలర్ట్ పంపబడుతుంది. ఫోటోలు మరియు ఒక వాయిస్ నోట్ ఐచ్ఛికం.",
        description:
          "ప్లాంటేషన్‌లో అత్యవసర ప్రమాదం లేదా కార్మికుల ఆపద పరిస్థితికి అత్యంత వేగవంతమైన అలర్ట్.",
        submitLabel: "అత్యవసర అలర్ట్ పంపండి",
      },
      report_incident: {
        label: "ఘటన నివేదిక",
        headline: "ప్లాంటేషన్ ఘటన నివేదికను పంపండి",
        helperText:
          "జోన్, సమస్య రకం, సంప్రదింపు వివరాలు, మరియు ఐచ్ఛిక మీడియాను నిర్మిత విధంగా నమోదు చేయండి.",
        description:
          "ప్లాంటేషన్ కార్యకలాపాలు మరియు ఫాలో-అప్ కోసం నిర్మిత ఘటన నివేదిక.",
        submitLabel: "ఘటన నివేదికను సమర్పించండి",
        confirmation: {
          title: "ఈ ప్లాంటేషన్ ఘటన నివేదికను ఇప్పుడే పంపాలా?",
          body: "ఒకసారి ధృవీకరించండి. నిర్మిత ప్లాంటేషన్ ఘటన నివేదిక వెంటనే పంపబడుతుంది.",
          actionLabel: "ఘటన నివేదికను సమర్పించండి",
        },
      },
    },
    fields: {
      severity: {
        label: "తీవ్రత",
        optionLabels: {
          minor: "స్వల్పం",
          moderate: "మధ్యస్థం",
          severe: "తీవ్రమైనది",
          critical: "అత్యంత అత్యవసరం",
        },
      },
      issueType: {
        label: "సమస్య రకం",
        optionLabels: {
          medical: "కార్మిక గాయం లేదా అత్యవసర స్థితి",
          hazard: "పరికరం లేదా విద్యుత్ ప్రమాదం",
          fire: "మంట లేదా పొగ",
          theft: "అడవి జంతువు లేదా చొరబాటు ప్రమాదం",
          breakdown: "వాహనం లేదా మార్గం ఆటంకం",
          accident: "ఇతర ప్లాంటేషన్ ఘటన",
        },
      },
      locationDescription: {
        label: "జోన్ లేదా సమీప గుర్తు",
        helperText:
          "GPS బలహీనంగా ఉంటే క్షేత్ర జోన్ పేరు, బ్లాక్ గుర్తు, రహదారి, లేదా సమీప గుర్తును ఉపయోగించండి.",
      },
      reporterPhone: {
        label: "తిరిగి కాల్ చేయాల్సిన నంబర్",
        helperText:
          "త్వరిత ఫాలో-అప్ అవసరమైతే పర్యవేక్షకులు ఈ సంఖ్యను ఉపయోగించవచ్చు.",
      },
      reporterName: {
        label: "మీ పేరు",
      },
      reporterEmail: {
        label: "మీ ఇమెయిల్",
      },
      plantationReference: {
        label: "ప్లాంటేషన్, కార్డ్ ఐడి, లేదా సైట్ సూచన",
      },
      siteContext: {
        label: "సైట్ సందర్భం",
        helperText:
          "ఎస్టేట్ పేరు, కార్మిక విభాగం, సేకరణ పాయింట్, లేదా డిప్లాయ్మెంట్ నోట్‌ను జోడించండి.",
      },
      description: {
        label: "ఏం జరిగింది?",
      },
      policeAssistanceRequested: {
        label: "పోలీసు లేదా భద్రత సహాయం అవసరం",
      },
      ambulanceRequested: {
        label: "వైద్య రవాణా అవసరం",
      },
    },
  },
  "hi-IN": {
    headline:
      "प्लांटेशन सुरक्षा अलर्ट या घटना रिपोर्ट भेजें",
    helperText:
      "प्लांटेशन के अलग-अलग ज़ोन से जल्दी सुरक्षा अलर्ट या घटना रिपोर्ट भेजने के लिए इस सरल प्रक्रिया का उपयोग करें। GPS उपलब्ध होने पर जोड़ा जाएगा। फ़ोटो और एक वॉइस नोट वैकल्पिक हैं।",
    subjectSummary: {
      title: "प्लांटेशन रिपोर्ट ज़ोन संदर्भ बनाए रखती हैं",
      description:
        "रिस्पॉन्डर के लिए ज़रूरी प्लांटेशन और ज़ोन संदर्भ को बनाए रखते हुए समस्या, स्थान और कॉलबैक नंबर जल्दी दर्ज करें।",
    },
    confirmation: {
      title: "क्या यह प्लांटेशन रिपोर्ट अभी भेजनी है?",
      body: "एक बार पुष्टि करें। प्लांटेशन रिपोर्ट तुरंत भेज दी जाएगी।",
      actionLabel: "पुष्टि करें और प्लांटेशन रिपोर्ट भेजें",
    },
    actions: {
      panic_alert: {
        label: "पैनिक अलर्ट",
        headline: "प्लांटेशन पैनिक अलर्ट भेजें",
        helperText:
          "हम तुरंत GPS लेने की कोशिश करेंगे और एक ही पुष्टि पर अलर्ट भेजेंगे। फ़ोटो और एक वॉइस नोट वैकल्पिक हैं।",
        description:
          "प्लांटेशन में तत्काल ख़तरे या श्रमिक संकट के लिए सबसे तेज़ अलर्ट प्रवाह।",
        submitLabel: "पैनिक अलर्ट भेजें",
      },
      report_incident: {
        label: "घटना रिपोर्ट",
        headline: "प्लांटेशन घटना रिपोर्ट भेजें",
        helperText:
          "ज़ोन, समस्या का प्रकार, संपर्क विवरण और वैकल्पिक मीडिया को संरचित तरीके से दर्ज करें।",
        description:
          "प्लांटेशन संचालन और फॉलो-अप के लिए संरचित घटना रिपोर्ट।",
        submitLabel: "घटना रिपोर्ट जमा करें",
        confirmation: {
          title: "क्या यह प्लांटेशन घटना रिपोर्ट अभी भेजनी है?",
          body: "एक बार पुष्टि करें। संरचित प्लांटेशन घटना रिपोर्ट तुरंत भेज दी जाएगी।",
          actionLabel: "घटना रिपोर्ट जमा करें",
        },
      },
    },
    fields: {
      severity: {
        label: "तत्कालता",
        optionLabels: {
          minor: "सामान्य",
          moderate: "मध्यम",
          severe: "गंभीर",
          critical: "अत्यंत गंभीर",
        },
      },
      issueType: {
        label: "समस्या का प्रकार",
        optionLabels: {
          medical: "श्रमिक चोट या संकट",
          hazard: "उपकरण या बिजली का ख़तरा",
          fire: "आग या धुआँ",
          theft: "वन्यजीव या घुसपैठ का ख़तरा",
          breakdown: "वाहन या रास्ता अवरोध",
          accident: "अन्य प्लांटेशन घटना",
        },
      },
      locationDescription: {
        label: "ज़ोन या नज़दीकी पहचान",
        helperText:
          "GPS कमज़ोर हो तो खेत का ज़ोन नाम, ब्लॉक मार्कर, सड़क, या नज़दीकी पहचान लिखें।",
      },
      reporterPhone: {
        label: "कॉलबैक नंबर",
        helperText:
          "तेज़ फॉलो-अप के लिए सुपरवाइज़र इस नंबर का उपयोग कर सकते हैं।",
      },
      reporterName: {
        label: "आपका नाम",
      },
      reporterEmail: {
        label: "आपका ईमेल",
      },
      plantationReference: {
        label: "प्लांटेशन, कार्ड आईडी, या साइट संदर्भ",
      },
      siteContext: {
        label: "साइट संदर्भ",
        helperText:
          "एस्टेट नाम, श्रमिक लाइन, कलेक्शन पॉइंट, या तैनाती नोट जोड़ें।",
      },
      description: {
        label: "क्या हुआ?",
      },
      policeAssistanceRequested: {
        label: "पुलिस या सुरक्षा सहायता चाहिए",
      },
      ambulanceRequested: {
        label: "चिकित्सा परिवहन चाहिए",
      },
    },
  },
}

function buildDualLanguageText(english: string, localized?: string | null) {
  const trimmedLocalized = localized?.trim()

  if (!trimmedLocalized) {
    return english
  }

  return `${english} / ${trimmedLocalized}`
}

function localizeActionDefinition(
  action: SafetyQrReportingTemplateActionDefinition,
  localizedAction:
    | Partial<SafetyQrReportingTemplateActionDefinition>
    | undefined
): SafetyQrReportingTemplateActionDefinition {
  return {
    ...action,
    label: buildDualLanguageText(action.label, localizedAction?.label),
    headline: buildDualLanguageText(action.headline ?? "", localizedAction?.headline),
    helperText: buildDualLanguageText(action.helperText ?? "", localizedAction?.helperText),
    description: buildDualLanguageText(action.description, localizedAction?.description),
    submitLabel: buildDualLanguageText(action.submitLabel, localizedAction?.submitLabel),
    confirmation: action.confirmation || localizedAction?.confirmation
      ? {
          actionLabel: buildDualLanguageText(
            action.confirmation?.actionLabel ?? "",
            localizedAction?.confirmation?.actionLabel
          ),
          body: buildDualLanguageText(
            action.confirmation?.body ?? "",
            localizedAction?.confirmation?.body
          ),
          title: buildDualLanguageText(
            action.confirmation?.title ?? "",
            localizedAction?.confirmation?.title
          ),
        }
      : undefined,
  }
}

function localizeFieldDefinition(
  field: SafetyQrReportingFieldDefinition,
  localizedField:
    | (Partial<SafetyQrReportingFieldDefinition> & {
        optionLabels?: Record<string, string>
      })
    | undefined
): SafetyQrReportingFieldDefinition {
  return {
    ...field,
    label: buildDualLanguageText(field.label, localizedField?.label),
    helperText: field.helperText || localizedField?.helperText
      ? buildDualLanguageText(field.helperText ?? "", localizedField?.helperText)
      : undefined,
    options: field.options?.map((option) => ({
      ...option,
      label: buildDualLanguageText(
        option.label,
        localizedField?.optionLabels?.[option.value]
      ),
    })),
  }
}

export function getPlantationLocalLanguageOption(
  code: string | null | undefined
) {
  if (!code) {
    return null
  }

  return (
    PLANTATION_LOCAL_LANGUAGE_OPTIONS.find((option) => option.code === code) ??
    null
  )
}

export function resolvePlantationPreferredLocalLanguageCode(
  lookup: PublicLookupData | null
): PlantationLocalLanguageCode | null {
  const registrationLanguageCode = lookup?.registration?.preferredLanguageCode

  if (
    registrationLanguageCode
    && getPlantationLocalLanguageOption(registrationLanguageCode)
  ) {
    return registrationLanguageCode as PlantationLocalLanguageCode
  }

  return null
}

export function localizeReportingTemplateForLookup(
  template: SafetyQrReportingTemplateDefinition | null,
  lookup: PublicLookupData | null
) {
  if (!template || template.id !== PLANTATIONS_TEMPLATE_ID) {
    return template
  }

  const localLanguageCode = resolvePlantationPreferredLocalLanguageCode(lookup)

  if (!localLanguageCode) {
    return template
  }

  const localization = plantationsLocalizationByCode[localLanguageCode]

  if (!localization) {
    return template
  }

  return {
    ...template,
    headline: buildDualLanguageText(template.headline, localization.headline),
    helperText: buildDualLanguageText(template.helperText, localization.helperText),
    subjectSummary: {
      title: buildDualLanguageText(
        template.subjectSummary.title,
        localization.subjectSummary?.title
      ),
      description: buildDualLanguageText(
        template.subjectSummary.description,
        localization.subjectSummary?.description
      ),
    },
    confirmation: {
      title: buildDualLanguageText(
        template.confirmation.title,
        localization.confirmation?.title
      ),
      body: buildDualLanguageText(
        template.confirmation.body,
        localization.confirmation?.body
      ),
      actionLabel: buildDualLanguageText(
        template.confirmation.actionLabel,
        localization.confirmation?.actionLabel
      ),
    },
    actions: template.actions.map((action) =>
      localizeActionDefinition(action, localization.actions[action.id])
    ),
    fields: template.fields.map((field) =>
      localizeFieldDefinition(field, localization.fields[field.id])
    ),
  }
}

export function listSafetyQrReportingTemplates() {
  return safetyQrReportingTemplateRegistry.templates
}

export function getSafetyQrReportingTemplate(templateId: string | null | undefined) {
  if (!templateId) {
    return null
  }

  return (
    listSafetyQrReportingTemplates().find((template) => template.id === templateId) ??
    null
  )
}

export function getSafetyQrReportingTemplateAction(
  template: SafetyQrReportingTemplateDefinition,
  actionId: string | null | undefined
) {
  if (!actionId) {
    return null
  }

  return template.actions.find((action) => action.id === actionId) ?? null
}

export function getInitialSafetyQrReportingTemplateAction(
  template: SafetyQrReportingTemplateDefinition
) {
  return template.actions[0] ?? null
}

export function buildReportingTemplateRouteKey(input: {
  kitVariant?: string | null
  productFamily?: string | null
  tagRole?: string | null
}) {
  if (!input.productFamily || !input.kitVariant || !input.tagRole) {
    return null
  }

  return `${input.productFamily}:${input.kitVariant}:${input.tagRole}`
}

export function resolveSafetyQrReportingTemplateForLookup(
  lookup: PublicLookupData | null
) {
  const routeKey = buildReportingTemplateRouteKey({
    productFamily: lookup?.kit?.productFamily ?? null,
    kitVariant: lookup?.kit?.kitVariant ?? null,
    tagRole: lookup?.physicalTag?.role ?? null,
  })

  if (!routeKey) {
    return null
  }

  const catalogEntry = resolveSafetyQrTagCatalogEntry({
    productFamily: lookup?.kit?.productFamily ?? null,
    kitVariant: lookup?.kit?.kitVariant ?? null,
    tagRole: lookup?.physicalTag?.role ?? null,
    tagSlot: lookup?.physicalTag?.tagSlot ?? null,
  })

  if (!catalogEntry) {
    return null
  }

  return getSafetyQrReportingTemplate(catalogEntry.tagDefinition.reportTemplateId)
}

export function isTemplateDrivenLookup(lookup: PublicLookupData | null) {
  return Boolean(resolveSafetyQrReportingTemplateForLookup(lookup))
}
